/*───────────────────────────────────────────────────────────────────────────*\
  File        : src/utils/analytics.ts
  Description : Small browser-side Google Analytics helpers for non-PII
                Meridian publication events.
\*───────────────────────────────────────────────────────────────────────────*/

export type MeridianAnalyticsEventName =
  | "meridian_pdf_preview_opened"
  | "meridian_pdf_request_submitted"
  | "meridian_pdf_download_clicked"

export interface MeridianAnalyticsEventParameters {
  readonly workSlug: string
  readonly workTitle: string
  readonly pagePath: string
}

type GtagCommand = "config" | "event" | "js"

type Gtag = (command: GtagCommand, target: string | Date, params?: Record<string, unknown>) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

const GA_MEASUREMENT_ID: string = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? ""

export function trackPageView(pagePath: string, pageTitle: string): void {
  const gtag: Gtag | undefined = getGtag()

  if (!gtag) {
    return
  }

  gtag("event", "page_view", {
    page_path: pagePath,
    page_title: pageTitle,
    send_to: GA_MEASUREMENT_ID,
  })
}

export function trackMeridianEvent(
  eventName: MeridianAnalyticsEventName,
  parameters: MeridianAnalyticsEventParameters,
): void {
  const gtag: Gtag | undefined = getGtag()

  if (!gtag) {
    return
  }

  gtag("event", eventName, {
    work_slug: parameters.workSlug,
    work_title: parameters.workTitle,
    page_path: parameters.pagePath,
  })
}

function getGtag(): Gtag | undefined {
  if (!GA_MEASUREMENT_ID || typeof window === "undefined") {
    return undefined
  }

  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer ?? []
    window.gtag = function queuedGtag(
      command: GtagCommand,
      target: string | Date,
      params?: Record<string, unknown>,
    ): void {
      const eventArguments: unknown[] = params === undefined
        ? [command, target]
        : [command, target, params]

      window.dataLayer?.push(eventArguments)
    }
  }

  return window.gtag
}

/*──────────────────────────── End of file ────────────────────────────────*/
